import { theme } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { selectLockersLogs } from 'reduxStore/lockersLogs/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { PersonProfileImage, TableFilterDropdown } from 'zComponents/atoms';
import { Flex, Table, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const AccessLogsContent = ({ deviceID }) => {
	const {
		token: { fontWeightStrong, fontSizeSM }
	} = theme.useToken();

	// Redux state
	const allLockersLogs = useSelector(selectLockersLogs);
	const selectedDevice = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const people = useSelector(selectPeople);
	const { loading } = useSelector((state) => state.LockersLogs);

	// Get the lockMac for the selected device
	const deviceLockMac = useMemo(() => {
		return selectedDevice?.fields?.find((f) => f.fieldTitle === 'Lock Mac')?.value || '';
	}, [selectedDevice]);

	// Filter logs by the device's lockMac
	const lockerLogs = useMemo(() => {
		if (!deviceLockMac) return [];
		return allLockersLogs.filter((log) => log.lockMac === deviceLockMac);
	}, [allLockersLogs, deviceLockMac]);

	// Fast lookup maps (O(1) access)
	const personNameById = useMemo(() => {
		const map = new Map();
		for (const p of people) {
			map.set(p.personID, p.displayName || p.name || '');
		}
		return map;
	}, [people]);

	const statusOptions = useMemo(() => {
		const statuses = lockerLogs.map((log) => (log.success ? 'Success' : 'Error'));
		const uniqueStatuses = [...new Set(statuses)];
		return uniqueStatuses.map((status) => ({ label: status, value: status }));
	}, [lockerLogs]);

	const peopleOptions = useMemo(() => {
		const uniquePersonIDs = [...new Set(lockerLogs.map((log) => log.personID).filter(Boolean))];
		return uniquePersonIDs
			.map((personID) => ({
				label: personNameById.get(personID) || personID,
				value: personID
			}))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [lockerLogs, personNameById]);

	const columns = [
		{
			title: 'DATE - TIME',
			dataIndex: 'createdDate',
			key: 'createdDate',
			render: (text) => dayjs(text).format('D/MM/YYYY - h:mm A'),
			sorter: (a, b) => dayjs(a.createdDate).valueOf() - dayjs(b.createdDate).valueOf(),
			disableFilter: true
		},

		{
			title: 'STATUS',
			dataIndex: 'success',
			key: 'status',
			render: (success) => {
				const status = success ? 'Success' : 'Error';
				const color = success ? 'success' : 'error';
				return (
					<Tag style={{ border: 'none', fontWeight: fontWeightStrong, fontSizeSM }} color={color}>
						{status}
					</Tag>
				);
			},
			sorter: (a, b) => {
				const statusA = a.success ? 'Success' : 'Error';
				const statusB = b.success ? 'Success' : 'Error';
				return statusA.localeCompare(statusB, undefined, { sensitivity: 'base' });
			},
			onFilter: (value, record) => {
				const status = record.success ? 'Success' : 'Error';
				return status === value;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={statusOptions} placeholder='Search status' />
			)
		},
		{
			title: 'PERSON',
			dataIndex: 'personID',
			key: 'person',
			render: (personID) => {
				const name = personNameById.get(personID) || '';
				const person = people.find((p) => p.personID === personID);
				return (
					<Flex align='center' gap={8}>
						<PersonProfileImage
							size={26}
							photoUrl={person?.photoUrl || undefined}
							name={name}
							entityName='People'
							shape='circle'
							edit={false}
						/>
						<Text>{name}</Text>
					</Flex>
				);
			},
			sorter: (a, b) =>
				(personNameById.get(a.personID) || '').localeCompare(personNameById.get(b.personID) || '', undefined, {
					sensitivity: 'base'
				}),
			onFilter: (value, record) => record.personID === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={peopleOptions} placeholder='Search people' />
			)
		}
	];

	return (
		<Table
			loading={loading}
			dataSource={lockerLogs}
			columns={columns}
			pagination={{ pageSize: 5 }}
			bordered={false}
		/>
	);
};

export default AccessLogsContent;
